/**
 * Provides a touch-friendly tap circle for continuous scrolling interactions.
 * タッチ操作向けのタップサークルを提供し、連続スクロール操作を実現するモジュール。
 */

import type { CSSProperties, ReactNode, PointerEvent as ReactPointerEvent } from "react"
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"

export type TapScrollCircleDragState = {
    /** Whether the pointer is actively dragging. / ポインターがドラッグ中かどうか。 */
    active: boolean
    /** Signed horizontal offset from the circle center. / サークル中心からの符号付き水平方向オフセット。 */
    offsetX: number
    /** Signed vertical offset from the circle center. / サークル中心からの符号付き垂直オフセット。 */
    offsetY: number
    /** Absolute distance between pointer and center. / ポインターと中心の距離。 */
    distance: number
    /** Drag direction relative to the circle. / サークルに対するドラッグ方向。 */
    direction: -1 | 0 | 1
}

export type TapScrollCircleProps = {
    /** Called whenever the drag state updates. / ドラッグ状態が更新された際に呼び出されるコールバック。 */
    onDragChange: (state: TapScrollCircleDragState) => void
    /** Additional class names for styling. / 追加のスタイル用クラス名。 */
    className?: string
    /** Maximum distance used for visual stretching. / 視覚的な伸縮に使用する最大距離。 */
    maxVisualDistance?: number
    /** Diameter of the circle in pixels. / サークルの直径 (ピクセル)。 */
    size?: number
    /** Additional inline styles. / 追加のインラインスタイル。 */
    style?: CSSProperties
    /** Base opacity applied to the circle. / サークル全体に適用する基準透明度。 */
    opacity: number
    /** Custom rendering logic for the visual contents. / ビジュアル内容を描画するカスタムロジック。 */
    renderVisual?: (props: TapScrollCircleRenderProps) => ReactNode
}

export type TapScrollCircleHandle = {
    /**
     * Resets the drag state and releases pointer capture if active.
     *
     * ドラッグ状態をリセットし、必要に応じてポインタキャプチャを解除する。
     */
    reset: () => void
    /**
     * Returns the underlying DOM element of the tap circle.
     *
     * タップサークルの DOM 要素を返す。
     */
    getElement: () => HTMLDivElement | null
}

const INITIAL_STATE: TapScrollCircleDragState = {
    active: false,
    offsetX: 0,
    offsetY: 0,
    distance: 0,
    direction: 0,
}

const DEAD_ZONE = 6

export type TapScrollCircleRenderProps = {
    /** Current drag state snapshot. / 現在のドラッグ状態のスナップショット。 */
    dragState: TapScrollCircleDragState
    /** Normalized distance within [0, 1]. / 正規化距離 (0 から 1 の範囲)。 */
    normalizedDistance: number
    /** Relative scale based on size. / サイズに基づく相対スケール。 */
    sizeScale: number
    /** Circle diameter in pixels. / サークル直径 (ピクセル)。 */
    size: number
    /** Applied opacity value. / 適用中の不透明度値。 */
    opacity: number
}

const defaultRenderVisual = ({ dragState, normalizedDistance }: TapScrollCircleRenderProps) => {
    const scale = 1 + normalizedDistance * 0.18
    const glow = 0.16 + normalizedDistance * 0.24
    const innerOpacity = 0.38 + normalizedDistance * 0.28
    const baseTransition = dragState.active ? "80ms ease-out" : "220ms ease"

    return (
        <>
            <div
                className="absolute inset-0 rounded-full"
                style={{
                    background: "linear-gradient(140deg, rgba(255,255,255,0.62), rgba(72,72,72,0.48))",
                    boxShadow: `0 0 0 1px rgba(255,255,255,0.28), 0 10px 22px rgba(0,0,0,${glow})`,
                    transform: `scale(${scale})`,
                    transition: `${baseTransition}, ${dragState.active ? "80ms" : "260ms"} box-shadow ease`,
                }}
            />
            <div
                className="absolute inset-[18%] rounded-full"
                style={{
                    background: "linear-gradient(140deg, rgba(255,255,255,0.72), rgba(28,28,28,0.58))",
                    boxShadow: "inset 0 4px 10px rgba(0,0,0,0.24), inset 0 0 2px rgba(255,255,255,0.55)",
                    opacity: innerOpacity,
                    transition: dragState.active ? "120ms opacity ease-out" : "220ms opacity ease",
                }}
            />
        </>
    )
}

/**
 * A circular touch handle that stretches toward the drag direction.
 * タップした方向へ伸縮するタッチ操作用サークル。
 */
export const TapScrollCircle = forwardRef<TapScrollCircleHandle, TapScrollCircleProps>(({ onDragChange, className, maxVisualDistance = 160, size = 40, style, opacity = 1, renderVisual }, ref) => {
    const [dragState, setDragState] = useState<TapScrollCircleDragState>(INITIAL_STATE)
    const pointerIdRef = useRef<number | null>(null)
    const centerRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
    const rootRef = useRef<HTMLDivElement>(null)

    /**
     * Updates drag state and notifies listeners.
     *
     * ドラッグ状態を更新してリスナーに通知。
     */
    const applyDragState = useCallback((nextState: TapScrollCircleDragState) => {
        setDragState(nextState)
        onDragChange(nextState)
    }, [onDragChange])

    const updateDragState = useCallback(
        (clientX: number, clientY: number, capture: boolean = false) => {
            const { x, y } = centerRef.current
            const offsetX = clientX - x
            const offsetY = clientY - y
            const distance = Math.abs(offsetY)
            const direction: -1 | 0 | 1 = distance < DEAD_ZONE ? 0 : offsetY < 0 ? -1 : 1
            applyDragState({
                active: capture || distance >= DEAD_ZONE,
                offsetX,
                offsetY,
                distance,
                direction,
            })
        },
        [applyDragState],
    )

    const releasePointerCapture = useCallback((pointerId: number | null) => {
        if (pointerId === null) {
            return
        }
        const element = rootRef.current
        if (element?.hasPointerCapture(pointerId)) {
            element.releasePointerCapture(pointerId)
        }
    }, [])

    const resetState = useCallback(
        (releasePointer: boolean = false) => {
            if (releasePointer) {
                releasePointerCapture(pointerIdRef.current)
            }
            pointerIdRef.current = null
            applyDragState(INITIAL_STATE)
        },
    [applyDragState, releasePointerCapture],
    )

    const handlePointerDown = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            event.preventDefault()
            event.stopPropagation()
            const element = rootRef.current ?? event.currentTarget
            const { left, top, width, height } = element.getBoundingClientRect()
            centerRef.current = { x: left + width / 2, y: top + height / 2 }
            pointerIdRef.current = event.pointerId
            element.setPointerCapture(event.pointerId)
            updateDragState(event.clientX, event.clientY, true)
        },
        [updateDragState],
    )

    const handlePointerMove = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            if (pointerIdRef.current !== event.pointerId) {
                return
            }
            event.preventDefault()
            updateDragState(event.clientX, event.clientY)
        },
        [updateDragState],
    )

    const handlePointerUp = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            if (pointerIdRef.current !== event.pointerId) {
                return
            }
            event.preventDefault()
            event.stopPropagation()
            resetState(true)
        },
        [resetState],
    )

    useImperativeHandle(
        ref,
        () => ({
            reset: () => {
                resetState(true)
            },
            getElement: () => rootRef.current,
        }),
        [resetState],
    )

    const clampedOpacity = Math.min(Math.max(opacity, 0), 1)
    const sizeScale = size / 64
    const normalized = Math.min(dragState.distance, maxVisualDistance) / maxVisualDistance
    const pullOffset = dragState.direction * normalized * 10 * sizeScale
    const visualRenderer = renderVisual ?? defaultRenderVisual
    const visualProps: TapScrollCircleRenderProps = {
        dragState,
        normalizedDistance: normalized,
        sizeScale,
        size,
        opacity: clampedOpacity,
    }
    const rootStyle: CSSProperties = {
        ...style,
        width: size,
        height: size,
        transform: `translateY(${pullOffset}px)`
    }
    rootStyle.opacity = clampedOpacity
    return (
        <div
            ref={rootRef}
            className={twMerge(
                "relative flex touch-none select-none items-center justify-center",
                "transition-transform duration-100 ease-out",
                className,
            )}
            style={rootStyle}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            role="presentation">
            {visualRenderer(visualProps)}
        </div>
    )
})

TapScrollCircle.displayName = "TapScrollCircle"
